import { AuthProvider, FeatureFlagProvider, useCurrentUser } from '@artic/api-client';
import { FeatureFlagsContext } from '@artic/ui/feature-flag';
import { PARTNER_COOKIE, readCookie } from '@artic/ui/lib/auth-cookies';
import { Center, Spinner } from '@chakra-ui/react';
import { Component, Suspense, useDeferredValue, type ErrorInfo, type ReactNode } from 'react';
import { useRecoilValue } from 'recoil';

import { partnerPicker } from '~/atoms/partnerPicker';

/**
 * Mounts the @artic/api-client provider stack (AuthProvider + FeatureFlagProvider)
 * for authenticated routes. AuthProvider calls /current-user via the api-client's
 * customFetch, which reads the artic_token cookie that App.tsx already populates
 * from the active Supabase session.
 *
 * Must sit INSIDE RequireAuth so /current-user is only called once a token exists.
 *
 * If /current-user fails (e.g. nest-backend down, network blocked) the boundary
 * keeps the rest of the hub usable by rendering children with an empty flag
 * context — feature-flag gated entries simply stay hidden in that case.
 */
export default function AuthenticatedProviders({ children }: { children: ReactNode }) {
  // Subscribe to the partner atom so this component re-renders when the admin
  // switches partner — otherwise FeatureFlagProvider stays pinned to the
  // previous partner_id and flag-gated nav entries don't update until a
  // hard refresh.
  //
  // Defer the propagation: the partner picker wraps its setState in
  // startTransition, but the standard useRecoilValue bypasses Recoil's
  // transition handling and re-renders synchronously. That makes the new
  // partner_id reach FeatureFlagProvider's useSuspenseQuery immediately and
  // trips the <Suspense fallback> below, unmounting AppLayout (and the
  // picker's portaled dropdown) mid-click. useDeferredValue restores the
  // transition: React holds the new tree in the background until the flags
  // arrive, the old UI stays committed, and the fallback never flashes.
  const partnerState = useRecoilValue(partnerPicker);
  const rawActivePartner = partnerState?.partner_key ?? readCookie(PARTNER_COOKIE);
  const activePartner = useDeferredValue(rawActivePartner);

  return (
    <ApiClientErrorBoundary fallbackChildren={children}>
      <Suspense fallback={<ProviderLoading />}>
        <AuthProvider activePartner={activePartner}>
          <FeatureFlagProviderWithPartner>{children}</FeatureFlagProviderWithPartner>
        </AuthProvider>
      </Suspense>
    </ApiClientErrorBoundary>
  );
}

function FeatureFlagProviderWithPartner({ children }: { children: ReactNode }) {
  const partnerId = usePartnerId();
  return <FeatureFlagProvider partnerId={partnerId}>{children}</FeatureFlagProvider>;
}

/**
 * Resolves the cookie's partner_key to the partner's UUID partner_id by matching
 * against /current-user's partners list. Mirrors quality's usePartnerId().
 */
function usePartnerId(): string | undefined {
  const { partners, activePartner } = useCurrentUser();
  if (partners.length === 0) return undefined;
  const matched = partners.find((p) => p.partner_key === activePartner);
  if (matched) return matched.partner_id;
  const byName = partners.find((p) => p.partner_name === activePartner);
  if (byName) return byName.partner_id;
  return partners[0]?.partner_id;
}

function ProviderLoading() {
  return (
    <Center h="100vh" w="100vw">
      <Spinner size="lg" />
    </Center>
  );
}

interface BoundaryProps {
  children: ReactNode;
  /** Rendered when the api-client providers fail to mount. */
  fallbackChildren: ReactNode;
}

interface BoundaryState {
  errored: boolean;
}

class ApiClientErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { errored: false };

  static getDerivedStateFromError(): BoundaryState {
    return { errored: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[AuthenticatedProviders] api-client provider crashed', error, info);
  }

  render(): ReactNode {
    if (this.state.errored) {
      // Backend unreachable or /current-user rejected — fall back to an empty
      // flag context so the rest of the hub stays functional. Any feature-flag
      // gated entries simply remain hidden until the issue is resolved.
      return (
        <FeatureFlagsContext.Provider value={{}}>
          {this.props.fallbackChildren}
        </FeatureFlagsContext.Provider>
      );
    }
    return this.props.children;
  }
}
